import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFRadio from "@/components/Input/Radio";
import VLFSelectNew from "@/components/Input/SelectNew";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import {PlayerAPI} from "@/api/player";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	genderOptions,
	REQUIRED_FIELD,
	sideOptions,
	playerRoles,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	statusOptions,
	playerSemaphoreOptions,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const HandlePlayerPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const userId = useSelector((state) => state?.user?.userId);
	const clubId = useSelector((state) => state?.user?.clubId);
	const userName = useSelector((state) => state?.user?.userName);

	const user = useSelector((state) => state?.user);

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		dateOfBirth: null,
		gender: "M",
		playerCategory: null,
		status: null,
	});

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		formatted: true,
	});

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			response.status = {value: response.status, label: response.status};

			if (response.semaphore) {
				response.semaphore = {
					value: response.semaphore,
					label: playerSemaphoreOptions.filter(
						(so) => so.value === response.semaphore,
					)?.[0]?.label,
				};
			}

			response.playerPrimaryRole = {
				value: response.goalkeeper ? "Portiere" : response.primaryRole,
				label: response.goalkeeper ? "Portiere" : response.primaryRole,
			};

			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (edit) {
			getPlayer();
		}
	}, [edit]);

	const getRequiredFields = () => {
		if (edit) {
			return ["firstName", "lastName", "status", "playerPrimaryRole"];
		}
		return [
			"firstName",
			"lastName",
			"status",
			"playerPrimaryRole",
			"playerCategory",
		];
	};

	const savePlayer = async () => {
		let errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			let body = Object.assign({}, fields);

			body = {
				...body,
				playerCategoryId: fields?.playerCategory?.value,
				dateOfBirth: fields.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				fatherHeight: fields?.fatherHeight || 0,
				motherHeight: fields?.motherHeight || 0,
				goalkeeper: fields?.playerPrimaryRole?.value === "Portiere",
				primaryRole: fields?.playerPrimaryRole?.value,
				clubId,
				userId,
				user: {userId, userName},
				status: fields?.status?.value,
				semaphore: fields?.semaphore?.value,
			};

			try {
				const response = edit
					? await PlayerAPI.update(id, body)
					: await PlayerAPI.create(body);
				setTimeout(() => {
					navigate(`/players/${response?.id || id}`);
					handleNotification(SUCCESSFUL_OPERATION, "success");
					setLoading(false);
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		savePlayer();
	};

	const getTextField = (
		fieldKey,
		label,
		required,
		values = fields,
		setValues = setFields,
		type = "text",
	) => {
		return (
			<VLFTextField
				type={type}
				value={values[fieldKey] || ""}
				onChange={(e) => {
					required && setErrors([]);
					setValues({...values, [fieldKey]: e.target.value});
				}}
				label={label}
				placeholder={label}
				required={required}
				size="small"
				helperText={errors.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""}
				error={errors.indexOf(fieldKey) !== -1}
			/>
		);
	};

	const goBack = () => {
		if (edit) {
			navigate("/players/" + id);
		} else {
			navigate("/players");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{
			label: "Giocatori",
			path: "/players",
		},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? (fields.firstName || "") + " " + (fields.lastName || "")
					: "",
				path: "/players/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										{getTextField("firstName", t("name"), true)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("lastName", t("surname"), true)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label={t("birthdate")}
											value={fields.dateOfBirth}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, dateOfBirth: value});
											}}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("placeOfBirth", t("birthplace"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("address", t("address"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("nationality", t("nationality"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("email", "Email", false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField(
											"telephone",
											t("phone"),
											false,
											fields,
											setFields,
											"phone",
										)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={statusOptions}
											sorted={false}
											value={fields.status}
											onChange={(newValue) => {
												setFields({
													...fields,
													status: newValue,
												});
											}}
											required
											label={t("condition")}
										/>
									</div>
									{user?.roleName === "Admin" ? (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={playerSemaphoreOptions}
												value={fields.semaphore}
												onChange={(newValue) => {
													setFields({
														...fields,
														semaphore: newValue,
													});
												}}
												label={t("zone")}
											/>
										</div>
									) : null}
									<div className="col-sm-12 col-md-6">
										<VLFRadio
											name="Genere"
											value={fields.gender || ""}
											onChange={(event) => {
												setFields({...fields, gender: event.target.value});
											}}
											options={genderOptions}
											label={t("gender")}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFRadio
											name="Piede"
											value={fields.favoriteFoot || ""}
											onChange={(event) => {
												setFields({
													...fields,
													favoriteFoot: event.target.value,
												});
											}}
											options={sideOptions(t)}
											label={t("favorite_foot")}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField(
											"fatherHeight",
											t("father_height") + " cm",
											false,
											fields,
											setFields,
											"number",
										)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField(
											"motherHeight",
											t("mother_height") + " cm",
											false,
											fields,
											setFields,
											"number",
										)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={categories.filter(
												(c) => c.label !== "Prima Squadra",
											)}
											sorted={false}
											value={
												edit ? fields.playerCategoryName : fields.playerCategory
											}
											onChange={(newValue) => {
												setErrors([]);
												setFields({
													...fields,
													playerCategory: newValue,
												});
											}}
											label={t("category")}
											required
											helperText={
												errors.indexOf("playerCategory") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("playerCategory") !== -1}
											editable={!edit}
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{/* <div className="field-label-container">
											<div className="field-label">Tipologia</div>
										</div> */}
										<FormControlLabel
											control={
												<Checkbox
													checked={fields?.boardingSchool || false}
													onChange={(event) =>
														setFields({
															...fields,
															boardingSchool: event.target.checked,
														})
													}
													inputProps={{"aria-label": "controlled"}}
												/>
											}
											label={t("boarding_school")}
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni ruolo"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={playerRoles}
											sortKey="order"
											value={fields.playerPrimaryRole}
											onChange={(newValue) => {
												setFields({
													...fields,
													playerPrimaryRole: newValue,
												});
											}}
											label={t("role")}
											required
										/>
									</div>
									{fields?.playerPrimaryRole?.value === "Portiere" ? (
										<div className="col-sm-12 col-md-6">
											<VLFRadio
												name="Mano"
												value={fields.favoriteHand || ""}
												onChange={(event) => {
													setFields({
														...fields,
														favoriteHand: event.target.value,
													});
												}}
												options={sideOptions(t)}
												label={t("favorite_hand")}
												size="small"
											/>
										</div>
									) : null}
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default HandlePlayerPage;
